import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, MessageCircle, Bot, UserCheck, Clock, AlertTriangle, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INTENT_LABELS = {
    faq_hours: "Hours",
    faq_location: "Location",
    faq_service: "Service info",
    faq_price: "Pricing",
    appointment_request: "Appointment",
    complaint: "Complaint",
    sales_enquiry: "Sales enquiry",
    human_handover_request: "Handover",
    medical_advice_seeking: "Medical advice",
    spam: "Spam",
    general: "General",
};

export default function WhatsAppReportSection({ businessId }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [days, setDays] = useState("30");

    const load = async () => {
        if (!businessId) return;
        setLoading(true);
        try {
            const r = await api.waReport(businessId, parseInt(days, 10));
            setReport(r.report);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [businessId, days]); // eslint-disable-line

    const fmt = (secs) => {
        if (secs == null) return "—";
        if (secs < 60) return `${Math.round(secs)}s`;
        return `${Math.round(secs / 60)}m`;
    };

    return (
        <div className="surface rounded-2xl p-6 space-y-4" data-testid="whatsapp-report-section">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                        <MessageCircle className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div>
                        <div className="label-eyebrow text-emerald-300">WhatsApp performance</div>
                        <h3 className="text-lg font-bold text-slate-100">Inbox summary</h3>
                    </div>
                </div>
                <Select value={days} onValueChange={setDays}>
                    <SelectTrigger data-testid="wa-report-days" className="w-40 h-9 bg-white/[0.03] border-white/10 text-slate-100 text-sm">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            ) : report ? (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Metric label="Total conversations" value={report.total_conversations} testid="wa-metric-total" />
                        <Metric label="AI-handled" value={report.ai_handled} accent="indigo" Icon={Bot} testid="wa-metric-ai" />
                        <Metric label="Human-handled" value={report.human_handled} accent="emerald" Icon={UserCheck} testid="wa-metric-human" />
                        <Metric label="Avg response" value={fmt(report.avg_response_seconds)} accent="cyan" Icon={Clock} testid="wa-metric-response" />
                        <Metric label="Unanswered" value={report.unanswered} accent="rose" Icon={AlertTriangle} testid="wa-metric-unanswered" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Metric label="Complaints" value={report.complaints} accent="rose" />
                        <Metric label="Appointment requests" value={report.appointments} accent="indigo" />
                        <Metric label="Leads / sales" value={report.leads} accent="emerald" />
                        <Metric label="AI resolution rate" value={`${Math.round((report.ai_resolution_rate || 0) * 100)}%`} accent="indigo" />
                    </div>
                    <div>
                        <div className="label-eyebrow mb-2">Most common enquiries</div>
                        <div className="flex flex-wrap gap-1.5" data-testid="wa-top-intents">
                            {(report.top_intents || []).map((t) => (
                                <Badge key={t.intent} variant="outline" className="border-white/10 bg-white/[0.03] text-slate-300 text-xs">
                                    {INTENT_LABELS[t.intent] || t.intent}
                                    <span className="ml-1.5 text-slate-500">· {t.count}</span>
                                </Badge>
                            ))}
                            {(!report.top_intents || report.top_intents.length === 0) && (
                                <span className="text-xs text-slate-500">No enquiries yet.</span>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <NarrativeCard title="What customers asked most" body={report.narrative?.what_customers_asked} testid="wa-narrative-asked" />
                        <NarrativeCard title="Needed staff intervention" body={report.narrative?.needed_staff_intervention} testid="wa-narrative-staff" />
                        <NarrativeCard title="FAQs to add" body={(report.narrative?.faqs_to_add || []).join(", ")} testid="wa-narrative-faqs" />
                    </div>
                </>
            ) : (
                <div className="text-sm text-slate-500 py-6 text-center">No WhatsApp data yet.</div>
            )}
        </div>
    );
}

function Metric({ label, value, accent = "indigo", Icon, testid }) {
    const map = {
        indigo: "border-indigo-500/30 bg-indigo-500/10 text-indigo-100",
        rose: "border-rose-500/30 bg-rose-500/10 text-rose-100",
        emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
        cyan: "border-cyan-500/30 bg-cyan-500/10 text-cyan-100",
    };
    return (
        <div data-testid={testid} className={`rounded-xl border p-3 ${map[accent] || map.indigo}`}>
            <div className="label-eyebrow flex items-center gap-1.5">
                {Icon && <Icon className="h-3 w-3" />}
                {label}
            </div>
            <div className="mt-1 text-lg font-bold">{value ?? "—"}</div>
        </div>
    );
}

function NarrativeCard({ title, body, testid }) {
    return (
        <div data-testid={testid} className="rounded-xl bg-white/[0.02] border border-white/10 p-3">
            <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-slate-500 mb-1.5">
                <Sparkles className="h-3 w-3" /> {title}
            </div>
            <div className="text-xs text-slate-300 leading-relaxed">{body || "—"}</div>
        </div>
    );
}
